import { replaceTagsWithNumbers } from './text';

/**
 * Prepares assistant message content for <Markdown> rendering:
 * converts our tag syntax into HTML spans the markdown overrides pick up,
 * and neutralizes underscores that markdown-to-jsx would otherwise read as
 * emphasis (e.g. FOREIGN_PATENT, snake_case).
 *
 * Bold/italic markers are deliberately left untouched — markdown-to-jsx
 * handles tags adjacent to, or nested inside, ** ** on its own.
 */

const TAG_TYPES = 'PATENT|PUBLICATION|APPLICATION|FOREIGN_PATENT';
const OTHER_TAG_TYPES = 'UPLOAD|MPEP|CFR|USC';

// Fenced blocks, inline code, and HTML markup are left alone when escaping.
const PROTECTED_SEGMENT = /(```[\s\S]*?```|`[^`\n]*`|<[^>]*>)/g;

const toTitleCaseDocType = (docType: string): string => {
  if (!docType) return docType;

  if (docType.toLowerCase() === 'pros_doc') return 'Prosecution Doc';
  if (docType.toLowerCase() === 'foreign_patent') return 'Foreign Patent';

  const upperCaseTypes = ['cfr', 'mpep', 'usc', 'epo', 'wipo', 'uspto', 'upload'];
  if (upperCaseTypes.includes(docType.toLowerCase())) return docType.toUpperCase();

  return docType.charAt(0).toUpperCase() + docType.slice(1).toLowerCase();
};

/**
 * Escape underscores that sit between word characters. CommonMark ignores
 * intraword `_`, but markdown-to-jsx italicizes it, which turns
 * "snake_case_name" into "snake<em>case</em>name".
 */
export const escapeIntrawordUnderscores = (text: string): string =>
  text
    .split(PROTECTED_SEGMENT)
    .map((segment, index) =>
      // Odd indices are the captured (protected) segments
      index % 2 === 1 ? segment : segment.replace(/(\w)_(?=\w)/g, '$1\\_')
    )
    .join('');

export const preprocessAssistantMarkdown = (text: string): string => {
  // Normalize line endings (handle \r\n)
  let out = text.replace(/\r\n/g, '\n');

  // Document tags -> spans (with underscore after the type, or without)
  out = out.replace(new RegExp(`\\[(${TAG_TYPES})_?([^\\]]+)\\]`, 'g'), (_match, docType, docId) => {
    const titleCaseType = toTitleCaseDocType(docType);
    return `<span class="doc-tag" data-doc-type="${docType}" data-doc-id="${docId}">${titleCaseType} ${docId}</span>`;
  });

  // Other document tags (UPLOAD, MPEP, CFR, USC) -> spans
  out = out.replace(new RegExp(`\\[(${OTHER_TAG_TYPES})_?([^\\]]+)\\]`, 'g'), (_match, docType, docId) => {
    const titleCaseType = toTitleCaseDocType(docType);
    return `<span class="doc-tag" data-doc-type="${docType}" data-doc-id="${docId}">${titleCaseType} ${docId}</span>`;
  });

  // Task tags -> spans
  out = out.replace(/\[Task:\s*([^#\]]+)(?:#([^\]]+))?\]/g, (_match, taskName, taskId) => {
    const cleanTaskName = taskName.trim();
    return `<span class="task-tag" data-task-name="${cleanTaskName}" ${taskId ? `data-task-id="${taskId}"` : ''}>${cleanTaskName}</span>`;
  });

  // Task result tags -> spans ([Task Result: taskName#id#taskType | Document ID: docId])
  out = out.replace(
    /\[Task Result:\s*([^#]+)#([^#]+)#([^|\]]+)(?:\s*\|\s*Document ID:\s*([^\]]+))?\]/g,
    (_match, taskName, taskId, taskType, docId) => {
      const cleanTaskName = taskName.trim();
      const cleanDocId = docId ? docId.trim() : '';
      const displayText = cleanDocId ? `${cleanTaskName} (Doc: ${cleanDocId})` : cleanTaskName;
      return `<span class="task-tag" data-task-name="${cleanTaskName}" data-task-id="${taskId.trim()}" data-task-type="${taskType.trim()}" data-doc-id="${cleanDocId}">${displayText}</span>`;
    }
  );

  // <Rsrc /> tags -> rsrc-link spans (grouped)
  out = replaceTagsWithNumbers(out);

  // Runs last so generated markup and attributes are already protected
  return escapeIntrawordUnderscores(out);
};
